import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate } from '../../utils/formatters.js';

import { ReturnCreateModal } from './ReturnCreateModal.jsx';
import { ReturnDetailModal } from './ReturnDetailModal.jsx';

const STATUS_VARIANT = { REQUESTED: 'warning', APPROVED: 'info', RECEIVED: 'success', REJECTED: 'danger' };

export function ReturnsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['sales', 'returns'], queryFn: () => salesApi.listReturns().then((r) => r.items) });
  const rows = list.data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;

  const columns = [
    { key: 'returnNumber', label: t('ret.col.number'), render: (r) => <span className="mono">{r.returnNumber}</span> },
    { key: 'created', label: t('ret.col.date'), render: (r) => formatDate(r.createdAt) },
    { key: 'reason', label: t('ret.col.reason'), render: (r) => r.reason || '-' },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`ret.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('ret.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('ret.title')}</h1>
          <p className="muted">{t('ret.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('ret.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('ret.metric.requested')} value={String(count('REQUESTED'))} trendVariant={count('REQUESTED') > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('ret.metric.approved')} value={String(count('APPROVED'))} />
        <MetricCard label={t('ret.metric.received')} value={String(count('RECEIVED'))} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('ret.empty')} />

      <ReturnCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ReturnDetailModal returnId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
